/* eslint-disable no-nested-ternary */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  useCallback, useRef, useState,
} from 'react';
import { v4 } from 'uuid';
import { AxiosError } from 'axios';
import STRING_KEYS from '../../language/keys';
import { X } from '../../icons/x';
import {
  useGetMissingContactDetailsStatus, useGetTalent,
  useRequestMissingContactDetails,
  useUpdateContactDetails,
} from '../../hooks/useTalent';
import { ErrorsStack } from '../ErrorsStack';
import { Loader } from '../Loader';
import { CONTACT_INFO_DETAILS_STATUS } from '../../consts/enums';
import { useAuth } from '../../hooks/useAuth';
import { PERMISSIONS } from '../../consts/permissions';
import { ContactDetailsMenuList } from './ContactDetailsMenuList';
import { SelectIconStyle } from './style';
import { ContactDetailsCard } from './ContactDetailsCard';
import { validateBasicDetails } from './validations';
import { ContactDetailsConfirmationMessage } from './ContactDetailsConfirmationMessage';

export type ContactDetail = {
  emails: EmailItem[],
  phones: PhoneItem[],
}

type ContactDetailsModalProps = {
  currentTalentId: string,
  isOpen: boolean,
  onClose: () => void,
  onUpdate?: (data:ContactDetail) => void,
}

const QUERY_KEY = 'contact-details-modal';

export default function ContactDetailsModal({
  currentTalentId,
  isOpen,
  onClose,
  onUpdate,
}: ContactDetailsModalProps): JSX.Element {
  const { t: translate } = useTranslation();
  const [formErrors, setFormErrors] = useState<ContactDetail>({} as ContactDetail);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState<boolean>(false);
  const [requestedContactDetails, setRequestedContactDetails] = useState(false);
  const [valuesField, setValuesFiled] = useState<ContactDetail>(
    {
      emails: [],
      phones: [],
    },
  );

  const arrEmails = valuesField.emails;
  const arrPhones = valuesField.phones;

  const { isFetching, refetch: refetchGetTalent } = useGetTalent(
    currentTalentId,
    (talent:FormattedTalentResponse) => {
      const updateEmailsPhones = {
        emails: talent.emails || [],
        phones: talent.phoneNumbers || [],
      };
      if (typeof onUpdate === 'function') {
        onUpdate(updateEmailsPhones);
      }
      setValuesFiled(updateEmailsPhones);
    },
    false,
    QUERY_KEY,
  );
  const callOnce = useRef(false);
  const { hasPermissions } = useAuth();

  const canRequestMissingContactDetails = arrEmails.length <= 0 && arrPhones.length <= 0;

  const showGetMissingContactDetails = canRequestMissingContactDetails && hasPermissions(
    [PERMISSIONS.TALENT_CONTACT_DETAILS_CREATE]);

  const getMissingContactDetails = useGetMissingContactDetailsStatus(
    (contactDetails: MissingContactDetails) => {
      if (contactDetails?.status === CONTACT_INFO_DETAILS_STATUS.FINISHED &&
        !callOnce.current
      ) {
        refetchGetTalent();
        callOnce.current = true;
      }
    }, currentTalentId, isOpen && canRequestMissingContactDetails);

  const requestMissingContactDetails = useRequestMissingContactDetails(() => {
    getMissingContactDetails.refetch();
  });

  const updateTalent = useUpdateContactDetails((data: ContactDetails) => {
    const emails = data.emails as string[];
    const phoneNumbers = data.phoneNumbers as string[];

    const changedObject = {
      emails: emails?.map((item: string) => ({
        id: v4(),
        value: item,
      })) || [],
      phones: phoneNumbers?.map((item: string) => ({
        id: v4(),
        value: item,
      })) || [],
    };
    setValuesFiled(changedObject);
    if (onUpdate) {
      onUpdate(changedObject);
    }
    setShowConfirmationMessage(true);
  }, currentTalentId);

  const editPhoneArrayHandler = useCallback((updatedItem: PhoneItem) => {
    const copiedPhoneArray = [...valuesField.phones];
    const updatedPhoneArray = copiedPhoneArray.map((item:PhoneItem) => {
      return item.id === updatedItem.id ? updatedItem : item;
    });

    setValuesFiled((prev: ContactDetail) => {
      return {
        ...prev,
        phones: [...updatedPhoneArray],
      };
    });
  }, [valuesField.phones]);

  const editEmailArrayHandler = useCallback((updatedItem: EmailItem) => {
    const copiedEmailArray = [...valuesField.emails];
    const updatedEmailArray = copiedEmailArray.map((item:EmailItem) => {
      return item.id === updatedItem.id ? updatedItem : item;
    });

    setValuesFiled((prev: ContactDetail) => {
      return {
        ...prev,
        emails: [...updatedEmailArray],
      };
    });
  }, [valuesField.emails]);

  const handleAddEmailRow = useCallback(() => {
    setValuesFiled((prev:ContactDetail) => {
      return {
        ...prev,
        emails: [...prev.emails, {
          id: v4(),
          value: '',
        }],
      };
    });
  }, []);

  const handleAddPhoneRow = useCallback(() => {
    setValuesFiled((prev:ContactDetail) => {
      return {
        ...prev,
        phones: [
          ...prev.phones, {
            id: v4(),
            value: '',
          },
        ],
      };
    });
  }, []);

  const handleDeleteEmailRow = useCallback((id: string) => {
    setValuesFiled((prev:ContactDetail) => {
      return {
        ...prev,
        emails: prev.emails.filter((item:EmailItem) => item.id !== id),
      };
    });
  }, []);

  const handleDeletePhoneRow = useCallback((id: string) => {
    setValuesFiled((prev:ContactDetail) => {
      return {
        ...prev,
        phones: prev.phones.filter((item:PhoneItem) => item.id !== id),
      };
    });
  }, []);

  const onUpdateClick = () => {
    const errorsObject = validateBasicDetails(valuesField);
    setFormErrors(errorsObject);
    if (Object.keys(errorsObject).length === 0) {
      updateTalent.mutate(valuesField, currentTalentId);
    }
  };

  const error: AxiosError = updateTalent.isError && updateTalent.error;

  const renderError = ():string => {
    if (getMissingContactDetails.isError) {
      const errorGetMissingContactDetails = getMissingContactDetails.error as AxiosError;

      return errorGetMissingContactDetails.message ||
       errorGetMissingContactDetails?.response?.data.message ||
        translate(STRING_KEYS.CONTACT_INFO_STATUS_ERROR);
    }

    if (getMissingContactDetails.data?.status === CONTACT_INFO_DETAILS_STATUS.FAILED) {
      return translate(STRING_KEYS.CONTACT_INFO_STATUS_ERROR);
    }

    return '';
  };

  const handleRequestMissingContactDetails = () => {
    setRequestedContactDetails(true);
    requestMissingContactDetails.mutate(currentTalentId);
  };

  return (
    <Dialog
      open={ isOpen }
      onClose={ onClose }
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
    >
      <DialogTitle
        style={ SelectIconStyle.dialogTitle.style }
        sx={ SelectIconStyle.dialogTitle.sx }
      >
        <Typography>{translate(STRING_KEYS.CONTACT_INFO_REQUEST)}</Typography>
        <X
          style={ SelectIconStyle.close }
          onClick={ onClose }
        />
      </DialogTitle>
      <DialogContent style={ SelectIconStyle.dialogContent } sx={ SelectIconStyle.scroll }>
        {updateTalent.isLoading || isFetching ? <Loader top="45%" left="45%" /> : (
          showConfirmationMessage ? <ContactDetailsConfirmationMessage /> :  (
            <>
              {updateTalent.isError && (
                <Box sx={ SelectIconStyle.errorBox.sx }>
                  <ErrorsStack
                    errors={ error?.response?.data?.fieldsErrors }
                    errorMessage={
                      error?.response?.data?.message ||
                      error?.message ||
                      translate(STRING_KEYS.DEFAULT_ERROR_MESSAGE)
                    }
                  />
                </Box>
              )}
              <ContactDetailsCard
                arrEmails={ arrEmails }
                arrPhones={ arrPhones }
                editEmailArrayHandler={ editEmailArrayHandler }
                handleDeleteEmailRow={ handleDeleteEmailRow }
                editPhoneArrayHandler={ editPhoneArrayHandler }
                handleDeletePhoneRow={ handleDeletePhoneRow }
                formErrors={ formErrors }
                errorMessage={ renderError() }
                isErrorGetMissingContactDetails={ getMissingContactDetails.isError }
                status={ getMissingContactDetails.data?.status || '' }
                onRequestMissingContactDetailsClick={ handleRequestMissingContactDetails }
                showGetMissingContactDetails={
                  showGetMissingContactDetails || requestedContactDetails
                }
                isDisabled={ requestMissingContactDetails.isLoading }
              />
            </>
          )
        )}
        {!(updateTalent.isLoading || isFetching || showConfirmationMessage) && (
          <ContactDetailsMenuList
            onEmailClick={ handleAddEmailRow }
            onPhoneClick={ handleAddPhoneRow }
          />
        )}
      </DialogContent>
      <DialogActions>
        {showConfirmationMessage ? (
          <Button variant="contained"
            onClick={ onClose }
            style={ { marginRight: '16px' } }
          >{translate(STRING_KEYS.DONE)}
          </Button>
        ) : (
          <>
            <Button onClick={ onClose }>{translate(STRING_KEYS.CANCEL)}</Button>
            <Button variant="contained"
              onClick={ onUpdateClick }
              style={ { marginRight: '16px' } }
            >{translate(STRING_KEYS.UPDATE_CONTACT_INFO)}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

ContactDetailsModal.defaultProps = {
  onUpdate: null,
};
